import { useState, useEffect, useCallback, useContext } from "react";
import ViewReport from "../../components/ViewReport/ViewReport";
import ApiService from "../../services/ApiService";
import { IAlert, IAssignee, IClinic, IDoctor, IReport, IReportType, ITextInput } from "../../helpers/Interfaces";
import Show from "../../components/Show/Show";
import { ReportFilterTypes } from "../../helpers/Types";
import DeletePrompt from "../../components/DeletePrompt/DeletePrompt";
import InputDropDown from "../../components/InputDropDown/InputDropDown";
import DateInput from "../../components/DateInput/DateInput";
import DocumentButton from "../../components/DocumentButton/DocumentButton";
import moment, { Moment } from "moment";
import DownloadOrange from "../../assets/images/DownloadOrange.svg";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { useNavigate } from "react-router-dom";
import { getTotalMinutes, isInRole } from "../../helpers/HelperFunctions";
import { User } from "../../store/Store";
import EditReport from "../../components/EditReport/EditReport";
import ReportsUI from "./ReportsUI";
import BlueHomeDot from "../../assets/images/BlueHomeDot.svg";
import BlueHomeDotDisable from "../../assets/images/BlueHomeDotDisable.svg";
import { Checkbox } from "@mui/material";
import { Colors } from "../../helpers/Colors";
import MuiAlert from "../../components/Alert/Alert";

import "./Reports.scss";

export default function Reports() {

    const apiService = new ApiService();

    const [reportModals, setReportModals] = useState({ view: false, bulkDelete: false, edit: false });
    const [loading, setLoading] = useState(false);
    const [reports, setReports] = useState([] as IReport[]);
    const [filteredReports, setFilteredReports] = useState([] as IReport[]);
    const [tickedReports, setTickedReports] = useState([] as string[]);
    const [selectedReport, setSelectedReport] = useState({} as IReport);
    const [showAlert, setShowAlert] = useState({} as IAlert);
    const [permission, setPermission] = useState([] as string[]);
    const [formData, setFormData] = useState
        <{
            statuses: number[], doctorId: null | string, clinicId: null | string, reportTypeId: null | string,
            assigneeId: null | string, fromDate: Moment, toDate: Moment
        }>
        ({
            statuses: [], doctorId: null, clinicId: null, reportTypeId: null, assigneeId: null,
            fromDate: moment().utc().subtract(30, 'days').local(), toDate: moment().utc().local(),
        });
    const [doctors, setDoctors] = useState([] as ITextInput[]);
    const [clinics, setClinics] = useState([] as ITextInput[]);
    const [reportTypes, setReportTypes] = useState([] as ITextInput[]);
    const [scribers, setScribers] = useState([] as ITextInput[]);
    const [selectedDateFilter, setSelectedDateFilter] = useState<"createdDate" | "dueDate" | "submittedDate" | "completedDate">("dueDate");
    const [filterState, setFilterState] = useState<"ASCENDING" | "DESCENDING" | null>(null);

    const statuses: { "label": string, "value": number }[] = [{ "label": "DRAFT", "value": 0 }, { "label": "PENDING", "value": 1 },
    { "label": "ASSIGNED", "value": 2 }, { "label": "SUBMITTED", "value": 3 }, { "label": "COMPLETED", "value": 4 }]

    const [user] = useContext(User);

    const navigate = useNavigate();

    useEffect(() => {
        if (!isInRole(user?.roles, "Admin")) {
            return navigate("/dashboard/reports");
        }
    }, [user])

    useEffect(() => {
        getPermissions();
        getDoctors();
        getAssignees();
    }, [])

    useEffect(() => {
        if (formData.doctorId) {
            getClinics();
            getReportTypes();
        }

        if (formData.doctorId && formData.clinicId) {
            getReportTypes();
        }
    }, [formData.doctorId, formData.clinicId])

    useEffect(() => {
        setFilteredReports(sortReports(reports));
    }, [reports])

    useEffect(() => {
        if (filterState === null) {
            setFilteredReports(sortReports(reports));
        }
    }, [filterState])

    function resetDefault() {
        setFilteredReports(sortReports(reports));
    }

    function filter(filterBy: ReportFilterTypes, query: string) {
        let filteredList = filteredReports.filter((report) => {
            return report[filterBy] === query;
        })

        setFilteredReports(filteredList);
    }

    function getDoctors() {

        setLoading(true);

        apiService.getDoctors().then((result) => {

            let data = result.data;

            if (!data?.isSuccessful) {
                let newShowAlert = showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = data?.errorMessage;
                newShowAlert.open = true;
                setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
                return;
            }

            getDoctorTextInput(data?.doctors);
        }).catch(() => {
            let newShowAlert = showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
            setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        }).finally(() => {
            setLoading(false);
        });
    }

    function getClinics() {

        setLoading(true);

        let data = {
            doctorId: formData.doctorId
        }

        apiService.getClinicsByDoctorId(data).then((result) => {

            let data = result.data;

            if (!data?.isSuccessful) {
                let newShowAlert = showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = data?.errorMessage;
                newShowAlert.open = true;
                setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
                return;
            }

            getClinicTextInput(data?.clinics);
        }).catch(() => {
            let newShowAlert = showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
            setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        }).finally(() => {
            setLoading(false);
        });
    }

    function getReportTypes() {

        setLoading(true);

        let data = {
            doctorId: formData.doctorId,
            clinicId: formData.clinicId
        }

        apiService.getReportTypesByDoctorIdOrClinicId(data).then((result) => {

            let data = result.data;

            if (!data?.isSuccessful) {
                let newShowAlert = showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = data?.errorMessage;
                newShowAlert.open = true;
                setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
                return;
            }

            getReportTypeTextInput(data?.reportTypes);
        }).catch(() => {
            let newShowAlert = showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
            setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        }).finally(() => {
            setLoading(false);
        });
    }

    function getAssignees() {

        setLoading(true);

        apiService.getAssignees().then((result) => {

            let data = result.data;

            if (!data?.isSuccessful) {
                let newShowAlert = showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = data?.errorMessage;
                newShowAlert.open = true;
                setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
                return;
            }

            getScribersTextInput(data?.users);
        }).catch(() => {
            let newShowAlert = showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
            setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        }).finally(() => {
            setLoading(false);
        });
    }

    function getScribersTextInput(assignees: IAssignee[]) {

        setLoading(true);

        let filteredAssignees = assignees.filter((assignee) => {
            return assignee.role === "Scribe"
        });

        let scribersArr = [] as ITextInput[];

        filteredAssignees.forEach(scriber => {
            let data = {
                label: scriber.name,
                value: scriber.email
            }

            scribersArr.push(data);
        });

        setScribers(scribersArr);

        setLoading(false);
    }

    function getDoctorTextInput(doctors: IDoctor[]) {

        setLoading(true);

        let doctorsArr = [] as ITextInput[];

        doctors.forEach(doctor => {
            let data = {
                label: doctor.firstName + " " + doctor.lastName,
                value: doctor.id
            }

            doctorsArr.push(data);
        });

        setDoctors(doctorsArr);

        setLoading(false);
    }

    function getClinicTextInput(clinics: IClinic[]) {

        setLoading(true);

        let clinicsArr = [] as ITextInput[];

        clinics.forEach(clinic => {
            let data = {
                label: clinic.clinicName,
                value: clinic.id
            }

            clinicsArr.push(data);
        });

        setClinics(clinicsArr);

        setLoading(false);
    }

    function getReportTypeTextInput(reportTypes: IReportType[]) {

        setLoading(true);

        let reportTypesArr = [] as ITextInput[];

        reportTypes.forEach(reportType => {
            let data = {
                label: `${reportType.reportName} - ${reportType.clinicName}`,
                value: reportType.id
            }

            reportTypesArr.push(data);
        });

        setReportTypes(reportTypesArr);

        setLoading(false);
    }

    function getPermissions() {

        setLoading(true);

        let data = {
            page: "Reports",
        };

        apiService.getPermission(data).then((result: any) => {

            let data = result.data;

            if (!data?.isSuccessful) {
                let newShowAlert = showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = data?.errorMessage;
                newShowAlert.open = true;
                setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
                return;
            }

            setPermission(data?.actions);
        }).catch(() => {
            let newShowAlert = showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
        }).finally(() => {
            setLoading(false);
        });
    }

    const handleOpen = (modal: "view" | "bulkDelete" | "edit") => {
        let newReportModals = reportModals;
        newReportModals[modal] = true;
        setReportModals((prevDetails) => ({ ...prevDetails, ...newReportModals }));
    }

    const handleClose = (modal: "view" | "bulkDelete" | "edit", shouldUpdate = false) => {
        let newReportModals = reportModals;
        newReportModals[modal] = false;
        setReportModals((prevDetails) => ({ ...prevDetails, ...newReportModals }));

        if (shouldUpdate) {
            searchReports();
        }
    }

    const sortReports = (reports: IReport[]): IReport[] => {
        const priorityOrder: { [key: string]: number } = {
            "S. Urgent": 1,
            "Urgent": 2,
            "24 Hours": 3,
            "48 Hours": 4,
            "72 Hours": 5
        };

        return reports.sort((a, b) => {
            const priorityA = priorityOrder[a.priority ?? ""] ?? Infinity;
            const priorityB = priorityOrder[b.priority ?? ""] ?? Infinity;

            if (priorityA !== priorityB) {
                return priorityA - priorityB;
            }

            const rawDateA = a.dueDate;
            const rawDateB = b.dueDate;

            const dateA = rawDateA ? new Date(rawDateA).getTime() : Infinity;
            const dateB = rawDateB ? new Date(rawDateB).getTime() : Infinity;

            return dateA - dateB;
        });
    };

    const sortReportsBySelectedDateFilter = (reports: IReport[]): IReport[] => {
        return reports.sort((a, b) => {
            const rawDateA = a[selectedDateFilter];
            const rawDateB = b[selectedDateFilter];

            const dateA = rawDateA ? new Date(rawDateA).getTime() : Infinity;
            const dateB = rawDateB ? new Date(rawDateB).getTime() : Infinity;

            return filterState === "ASCENDING" ? dateA - dateB : dateB - dateA;
        });
    };

    function tickReport(reportId: string) {
        setTickedReports(prev => {
            if (prev.includes(reportId)) {
                return prev.filter(id => id !== reportId);
            } else {
                return [...prev, reportId];
            }
        });
    }

    function toggleAllReports(allReportIds: string[]) {
        setTickedReports(prev => {
            const allSelected = allReportIds.every(id => prev.includes(id));
            return allSelected ? [] : [...allReportIds];
        });
    }

    function isReportTicked(reportId: string): boolean {
        return tickedReports.includes(reportId);
    }

    function areAllReportsTicked(ids: string[]): boolean {
        if (ids.length === 0) return false;
        return ids.every(id => tickedReports.includes(id));
    }

    function bulkDelete() {

        setLoading(true);

        const data = { "documentIds": tickedReports }

        apiService.bulkdDleteReports(data).then((result) => {

            let data = result.data;

            if (!data?.isSuccessful) {
                let newShowAlert = showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = data?.errorMessage;
                newShowAlert.open = true;
                setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
                return;
            }

            let newShowAlert = showAlert;
            newShowAlert.isError = false;
            newShowAlert.message = `${tickedReports.length} Document Deleted`;
            newShowAlert.open = true;
            setShowAlert((prevDetails: any) => ({ ...prevDetails, ...newShowAlert }));

            setTickedReports([]);
            handleClose("bulkDelete", true);
        }).catch(() => {
            let newShowAlert = showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
            setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        }).finally(() => {
            setLoading(false);
        });
    }

    const searchReports = useCallback(() => {
        setLoading(true);

        if (formData.statuses.length === 0) {
            setShowAlert((prevDetails) => ({ ...prevDetails, isError: true, message: "Please Select a Status", open: true }));
            setLoading(false);
            return;
        }

        let data = {
            statuses: formData.statuses,
            fromDate: moment(formData.fromDate).clone().toISOString(),
            toDate: moment(formData.toDate).clone().toISOString(),
            doctorId: formData.doctorId,
            clinicId: formData.clinicId,
            reportTypeId: formData.reportTypeId,
            assigneeId: formData.assigneeId,
        }

        apiService.getFilteredReports(data).then((result) => {

            let data = result.data;

            if (!data?.isSuccessful) {
                let newShowAlert = showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = data?.errorMessage;
                newShowAlert.open = true;
                setShowAlert((prevDetails) => ({ ...prevDetails, newShowAlert }));
                return;
            }

            if (data?.documents?.length === 0) {
                let newShowAlert = showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = "No Reports";
                newShowAlert.open = true;
                setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
            }

            setTickedReports([]);
            setReports(data?.documents);
        }).catch(() => {
        }).finally(() => {
            setLoading(false);
        });
    }, [formData])

    async function downloadReport() {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Sheet1');

        worksheet.columns = [
            { header: 'Patient Name', key: 'patientName', width: 15 },
            { header: 'Clinic', key: 'clinic', width: 15 },
            { header: 'Type', key: 'reportType', width: 15 },
            { header: 'Total Minutes', key: 'totalDuration', width: 15 },
            { header: 'Stage', key: 'stage', width: 15 },
            { header: 'Status', key: 'documentStatus', width: 15 },
            { header: 'Submitted Date', key: 'submittedDate', width: 25 },
            { header: 'Doctor', key: 'doctorName', width: 25 },
            { header: 'Place of Assessment', key: 'placeOfAssessment', width: 25 },
            { header: 'Date of Assessment', key: 'dateOfAssessment', width: 25 },
            { header: 'Referrer', key: 'referrer', width: 25 },
            { header: 'Priority/TAT', key: 'priority', width: 15 },
        ];

        let downloadReports = [...reports];

        downloadReports = downloadReports.sort((a, b) => {
            const rawDateA = a.submittedDate ?? a.dueDate;
            const rawDateB = b.submittedDate ?? b.dueDate;

            const dateA = rawDateA ? new Date(rawDateA).getTime() : Infinity;
            const dateB = rawDateB ? new Date(rawDateB).getTime() : Infinity;

            return dateA - dateB;
        });

        const processedData = downloadReports.map(item => ({
            ...item,
            dateOfAssessment: validDateField(item.dateOfAssessment),
            submittedDate: validDateField(item.submittedDate),
            totalDuration: convertDuration(item.totalDuration)
        }));
        
        worksheet.addRows(processedData);

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `${validDateField(new Date())}.xlsx`);
    }

    function convertDuration(duration: number) {
        return (duration / 60).toFixed(2);
    }

    function validDateField(value: Date | undefined) {
        if (value) {
            return moment.utc(value).local().format("DD/MMM/YYYY");
        } else {
            return " - "
        }
    }

    function handleDateClick(selectedDateFilter: "createdDate" | "dueDate" | "submittedDate" | "completedDate") {
        if (filterState === "ASCENDING") {
            setFilterState("DESCENDING");
            setFilteredReports(sortReportsBySelectedDateFilter(reports));
        } else if (filterState === "DESCENDING") {
            setFilterState(null);
        } else {
            setFilterState("ASCENDING");
            setFilteredReports(sortReportsBySelectedDateFilter(reports));
        }

        setSelectedDateFilter(selectedDateFilter);
    }

    return (<div className="search-reports-container">
        <MuiAlert error={showAlert.isError} message={showAlert.message} open={showAlert.open}
            onClose={() => {
                setShowAlert((prevDetails) => ({ ...prevDetails, open: false }));
            }} />
        <div className="filter-wrapper">
            <div className="filter-row">
                {statuses.map((status, index) => {
                    return (
                        <div className="status-wrapper" key={index + status.label}>
                            <Checkbox icon={<img alt='check box' src={BlueHomeDotDisable} />}
                                checkedIcon={<img alt='check' src={BlueHomeDot} />}
                                value="cryon" style={{ color: Colors.white_10 }}
                                checked={formData.statuses.includes(status.value)}
                                onClick={() => {
                                    if (formData.statuses.includes(status.value)) {
                                        setFormData({
                                            ...formData,
                                            statuses: formData.statuses.filter((item) => item !== status.value)
                                        });
                                    } else {
                                        setFormData({
                                            ...formData,
                                            statuses: [...formData.statuses, status.value]
                                        });
                                    }
                                }}
                            />
                            <span className="status">{status.label}</span>
                        </div>
                    )
                })}
            </div>
            <div className="filter-row">
                <InputDropDown title='DOCTOR' type='dropdown'
                    defaultValue={formData.doctorId} values={doctors}
                    onSelect={(value: any) => {
                        if (value?.length > 0) {
                            setFormData((prev) => ({ ...prev, doctorId: value[0].value }));
                        }
                    }} />
                <InputDropDown title='CLINIC' type='dropdown'
                    defaultValue={formData.clinicId} values={clinics}
                    onSelect={(value: any) => {
                        if (value?.length > 0) {
                            setFormData((prev) => ({ ...prev, clinicId: value[0].value }));
                        }
                    }} />
                <InputDropDown title='REPORT TYPE' type='dropdown'
                    defaultValue={formData.reportTypeId} values={reportTypes}
                    onSelect={(value: any) => {
                        if (value?.length > 0) {
                            setFormData((prev) => ({ ...prev, reportTypeId: value[0].value }));
                        }
                    }} />
                <InputDropDown title='SCRIBER' type='dropdown'
                    defaultValue={formData.assigneeId} values={scribers}
                    onSelect={(value: any) => {
                        if (value?.length > 0) {
                            setFormData((prev) => ({ ...prev, assigneeId: value[0].value }));
                        }
                    }} />
                <DateInput label="FROM*" value={formData.fromDate.toISOString()}
                    showError={false}
                    onChange={(value: any) => {
                        if (value) {
                            setFormData((prev) => ({ ...prev, fromDate: value }));
                        }
                    }} />
                <DateInput label="TO*" value={formData.toDate.toISOString()}
                    showError={false}
                    onChange={(value: any) => {
                        if (value) {
                            setFormData((prev) => ({ ...prev, toDate: value }));
                        }
                    }} />
                <DocumentButton loading={loading} onClick={() => { searchReports(); }} text={"Search"} />
                <img className="download-content" src={DownloadOrange} alt="Download" onClick={() => { downloadReport(); }} />
            </div>
        </div>
        <div className="stats-wrapper">
            <span>Total Reports: {filteredReports.length}</span>
            <span>Total Minutes: {getTotalMinutes(filteredReports)}</span>
        </div>
        <Show hasAccess={permission?.includes("Read")}>
            <div className="table-wrapper">
                <ReportsUI reports={filteredReports} setSelectedReport={setSelectedReport} openView={handleOpen}
                    filter={filter} showFilter={filteredReports.length !== reports.length} cancelFilter={resetDefault} tickReport={tickReport}
                    toggleAllReports={toggleAllReports} isReportTicked={isReportTicked} showDelete={tickedReports.length > 0}
                    areAllReportsTicked={areAllReportsTicked} searchReports={searchReports} handleDateClick={handleDateClick} filterState={filterState}
                    selectedDateFilter={selectedDateFilter} />
            </div>
        </Show>


        {reportModals.view
            ? (<ViewReport canDelete={permission?.includes("Delete")} open={reportModals.view} closeModal={handleClose} selectedReport={selectedReport}
                openModal={handleOpen} canEdit={permission?.includes("Update")} showAlert={setShowAlert} />)
            : (<></>)
        }
        {reportModals.edit
            ? (<EditReport open={reportModals.edit} close={handleClose} selectedReportId={selectedReport.id}
                showReport={setShowAlert} />)
            : (<></>)
        }
        {reportModals.bulkDelete
            ? (<DeletePrompt open={reportModals.bulkDelete} close={() => { handleClose("bulkDelete", false); }}
                header={tickedReports.length > 1 ? `Deleting ${tickedReports.length} Documents` : `Deleting ${tickedReports.length} Document`}
                onDelete={bulkDelete} loading={loading} body={"Please note that this action is irreversible. Are you certain you wish to proceed with this operation?"} />)
            : (<></>)
        }
    </div>)
}